import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { generateSecretKey, importRsaKey, setE2EEnabled, setPublicCryptoKey, setSecretCryptoKey } from '../crypto';

export interface CryptoPollProjection {
  publicKey?: string;
  useE2EEncryption?: boolean;
}

export type CryptoContextValue = {
  secretKey: CryptoKey | null;
  cryptoKey: CryptoKey | null;
  isE2EEnabled: boolean;
  isLoading: boolean;
};

const initialCryptoContextValue: CryptoContextValue = {
  isE2EEnabled: false,
  secretKey: null,
  cryptoKey: null,
  isLoading: true,
};

export const CryptoContext = createContext<CryptoContextValue>(initialCryptoContextValue);

export const useCryptoContext = () => useContext(CryptoContext);

type CryptoContextProviderProps = {
  poll: CryptoPollProjection;
  children: React.ReactNode | React.ReactNode[];
};

export const CryptoContextProvider: React.FC<CryptoContextProviderProps> = ({ poll, children }) => {
  const [cryptoContextValue, _setCryptoContextValue] = useState<CryptoContextValue>(initialCryptoContextValue);
  const setCryptoContextValue = useCallback(
    ({ cryptoKey, secretKey, isE2EEnabled }: Pick<CryptoContextValue, 'cryptoKey' | 'secretKey' | 'isE2EEnabled'>) => {
      if (cryptoKey != null) setPublicCryptoKey(cryptoKey);
      if (secretKey != null) setSecretCryptoKey(secretKey);
      _setCryptoContextValue({ cryptoKey, secretKey, isLoading: false, isE2EEnabled });
      setE2EEnabled(isE2EEnabled);
    },
    [],
  );

  useEffect(() => {
    if (poll.publicKey == null) {
      setCryptoContextValue({ cryptoKey: null, secretKey: null, isE2EEnabled: false });
      return;
    }

    Promise.all([importRsaKey(poll.publicKey), generateSecretKey()])
      .then(([cryptoKey, secretKey]) => {
        setCryptoContextValue({
          isE2EEnabled: !!poll.useE2EEncryption,
          cryptoKey,
          secretKey,
        });
      })
      .catch(() => {
        setCryptoContextValue({ cryptoKey: null, secretKey: null, isE2EEnabled: false });
      });
  }, []);

  return <CryptoContext.Provider value={cryptoContextValue}>{children}</CryptoContext.Provider>;
};
